import React, { useEffect, useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { HOST_HEADER_HEIGHT, IS_EMBEDDED, scrollHostPageToTimFrame, timCompactStyle, useTimLayoutScale } from "./timCompactLayout";

/**
 * Scoped MUI theme for the TIM pages. Dialogs, menus, poppers and tooltips are portalled to
 * <body>, outside `.projects-page`, but keep React context, so overrides in this theme reach
 * every TIM popup while popups elsewhere in the app are unaffected.
 *
 * Rule: never zoom an element whose position MUI/Popper.js sets in JS (Popper root, Popover
 * paper, Tooltip popper); zoom its content instead.
 */

// MUI Autocomplete sizes its popper from anchorEl.clientWidth, which ignores zoom on ancestors;
// use the anchor's rendered width instead.
const matchAnchorWidthModifier = {
  name: "timMatchAnchorWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }: { state: any }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
};

// Same clientWidth issue for the MUI Select menu min-width.
const fitSelectMenuToAnchor = (paper: HTMLElement) => {
  const anchor = document.querySelector('.MuiSelect-select[aria-expanded="true"]')?.parentElement;
  const width = anchor?.getBoundingClientRect().width;
  if (width) paper.style.minWidth = `${width}px`;
};

type ComponentOverrides = Record<string, { styleOverrides?: Record<string, unknown>; defaultProps?: Record<string, unknown> }>;

const buildTimComponents = (scale: number): ComponentOverrides => {
  const px = (value: number) => `${Number((value * scale).toFixed(2))}px`;
  return {
    MuiDialog: {
      styleOverrides: {
        // --tim-scale lets TimCompactLayout.scss compensate vh-based dialog sizes.
        paper: { "--tim-scale": scale, zoom: scale },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { "& > *": { zoom: scale } },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: { "&:not(.MuiTooltip-popper) > *": { zoom: scale } },
      },
    },
    // Tooltips are scaled explicitly: zooming them would misplace the Popper.js arrow.
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: `${px(4)} ${px(8)}`,
          fontSize: px(11),
          maxWidth: px(300),
          borderRadius: px(4),
          '.MuiTooltip-popper[data-popper-placement*="left"] &': { marginRight: px(14) },
          '.MuiTooltip-popper[data-popper-placement*="right"] &': { marginLeft: px(14) },
          '.MuiTooltip-popper[data-popper-placement*="top"] &': { marginBottom: px(14) },
          '.MuiTooltip-popper[data-popper-placement*="bottom"] &': { marginTop: px(14) },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: { popper: { modifiers: [matchAnchorWidthModifier] } },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: { TransitionProps: { onEntering: fitSelectMenuToAnchor } },
      },
    },
  };
};

const buildTimCompactTheme = (outerTheme: Theme, scale: number): Theme => {
  if (scale === 1) return outerTheme;
  const outerComponents: Record<string, any> = outerTheme.components || {};
  const components: Record<string, any> = { ...outerComponents };
  Object.entries(buildTimComponents(scale)).forEach(([name, config]) => {
    const base = outerComponents[name] || {};
    components[name] = {
      ...base,
      styleOverrides: { ...base.styleOverrides, ...config.styleOverrides },
      defaultProps: { ...base.defaultProps, ...config.defaultProps },
    };
  });
  return { ...outerTheme, components };
};

/** Provides the TIM layout scale to page CSS (--tim-scale) and to portal-rendered MUI components. */
export const TimCompactThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scale = useTimLayoutScale();
  const theme = useMemo(() => (outerTheme: Theme) => buildTimCompactTheme(outerTheme, scale), [scale]);
  useEffect(() => {
    if (!IS_EMBEDDED) return;
    const frame = requestAnimationFrame(scrollHostPageToTimFrame);
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <div style={timCompactStyle(scale)}>
        {/* Covered by the host page's fixed header once the iframe is scrolled to the top of the window. */}
        {IS_EMBEDDED && <div className="tim-host-header-space" aria-hidden="true" style={{ height: HOST_HEADER_HEIGHT }} />}
        {children}
      </div>
    </ThemeProvider>
  );
};

export function withTimCompactTheme<P extends object>(Component: React.ComponentType<P>) {
  // Memoized so a scale change only restyles (CSS variable + MUI theme) instead of re-rendering the page.
  const MemoComponent = React.memo(Component) as unknown as React.ComponentType<P>;
  const WithTimCompactTheme: React.FC<P> = (props) => (
    <TimCompactThemeProvider>
      <MemoComponent {...props} />
    </TimCompactThemeProvider>
  );
  WithTimCompactTheme.displayName = `withTimCompactTheme(${Component.displayName || Component.name || "Component"})`;
  return WithTimCompactTheme;
}